import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Spinner } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import Icon from "@mdi/react";
import { mdiArrowRight, mdiHelpCircleOutline, mdiPencilOutline } from "@mdi/js";
import PageLayout from "app/components/common/PageLayout";
import CustomToast from "app/components/common/CustomToast";
import UpgradePlanModal from "app/components/WebApp/GlobalNavBar/UpgradePlanModal";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import { usePartialLoading } from "app/hooks/usePartialLoading";
import {
  fetchSchedulingProfile,
  enableSchedulingService,
  disableSchedulingService,
  updateSchedulingProfile,
  clearEnableStatus,
  clearDisableStatus,
  clearUpdateStatus,
} from "./slice";
import { validateAll } from "./validationUtils";
import AvailabilityEditor from "./AvailabilityEditor";
import BookingRulesEditor from "./BookingRulesEditor";
import ServiceConfigEditor from "./ServiceConfigEditor";
import SaveSettingsModal from "./SaveSettingsModal";
import HowClientBookingWorksModal from "./HowClientBookingWorksModal";
import TurnOffBookingModal from "./TurnOffBookingModal";
import bannerImage from "app/images/scheduler-banner.svg";
import bannerImageTablet from "app/images/scheduler-banner-tablet.svg";
import bannerImageMobile from "app/images/scheduler-banner-mobile.svg";
import ClientViewPreview from "./ClientViewPreview";
import { DAYS_OF_WEEK } from "./constants";
import "./clientBookingSettings.css";

const slugify = (text) => {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 || 12;
  return `${String(displayHour).padStart(2, "0")}:${minutes} ${ampm}`;
};

const formatMinutes = (minutes) => {
  if (!minutes && minutes !== 0) return "N/A";
  if (minutes <= 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

const formatDays = (days) => {
  if (!days && days !== 0) return "N/A";
  if (days === 1) return "1 day";
  return `${days} days`;
};

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

const buildFormData = (profile) => ({
  weeklyHours: deepClone(profile.weekly_hours || {}),
  bufferTime: profile.min_buffer_time_minutes ?? 0,
  minNotice: profile.min_lead_time_minutes ?? 0,
  bookingWindow: profile.max_booking_horizon_days ?? 0,
  durationRules: deepClone(profile.duration_rules || []),
});

const ClientBookingSettings = () => {
  const dispatch = useDispatch();
  const {
    user: { profile: userProfile },
    device: { isMobile, isTablet },
  } = useApp();

  const {
    profile,
    fetchStatus,
    enableStatus,
    enableError,
    disableStatus,
    updateStatus,
    updateError,
  } = useSelector((state) => state.clientBookingSettings);

  const { startLoading, stopLoading } = usePartialLoading(
    "client-booking-settings"
  );

  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showTurnOffModal, setShowTurnOffModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSaveResultModal, setShowSaveResultModal] = useState(false);
  const originalFormDataRef = useRef(null);

  const isActive = profile?.is_active;

  useInitialFetch(() => dispatch(fetchSchedulingProfile()));

  useEffect(() => {
    if (fetchStatus === "loading") {
      startLoading();
    } else {
      stopLoading();
    }
  }, [fetchStatus, startLoading, stopLoading]);

  // Handle enable success
  useEffect(() => {
    if (enableStatus === "success") {
      setToast({
        show: true,
        message: "Client Booking is now active! Share your booking link.",
        variant: "success",
      });
      dispatch(clearEnableStatus());
      dispatch(fetchSchedulingProfile());
      setTimeout(() => {
        const bookingLinkBtn = document.querySelector(
          ".booking-link-wrapper .btn"
        );
        if (bookingLinkBtn) bookingLinkBtn.click();
      }, 500);
    } else if (enableStatus === "failed") {
      if (enableError?.subscriptionRequired) {
        setShowUpgradeModal(true);
      } else {
        setToast({
          show: true,
          message: "Failed to enable client booking. Please try again.",
          variant: "error",
        });
      }
      dispatch(clearEnableStatus());
    }
  }, [enableStatus, enableError, dispatch]);

  // Handle disable success
  useEffect(() => {
    if (disableStatus === "success") {
      setShowTurnOffModal(false);
      setToast({
        show: true,
        message: "Client Booking turned off. You can turn it on anytime.",
        variant: "success",
      });
      dispatch(clearDisableStatus());
      dispatch(fetchSchedulingProfile());
    } else if (disableStatus === "failed") {
      setToast({
        show: true,
        message: "Failed to turn off client booking. Please try again.",
        variant: "error",
      });
      dispatch(clearDisableStatus());
    }
  }, [disableStatus, dispatch]);

  // Handle update success/failure
  useEffect(() => {
    if (updateStatus === "success") {
      setIsEditMode(false);
      setEditFormData(null);
      setValidationErrors({});
      originalFormDataRef.current = null;
      setShowSaveResultModal(true);
      dispatch(clearUpdateStatus());
      dispatch(fetchSchedulingProfile());
    } else if (updateStatus === "failed") {
      if (updateError?.partialSuccess) {
        // Settings saved but re-enable failed — exit edit mode,
        // refresh profile, and warn the user.
        setIsEditMode(false);
        setEditFormData(null);
        setValidationErrors({});
        originalFormDataRef.current = null;
        dispatch(fetchSchedulingProfile());
        setToast({
          show: true,
          message: updateError.message,
          variant: "error",
        });
      } else {
        setToast({
          show: true,
          message: "Failed to save settings. Please try again.",
          variant: "error",
        });
      }
      dispatch(clearUpdateStatus());
    }
  }, [updateStatus, updateError, dispatch]);

  const handleTurnOn = useCallback(() => {
    if (userProfile?.subscription?.tier === "Free") {
      setShowUpgradeModal(true);
      return;
    }
    dispatch(enableSchedulingService());
  }, [dispatch, userProfile]);

  const handleTurnOff = useCallback(() => {
    dispatch(disableSchedulingService());
  }, [dispatch]);

  const handleCopyBookingLink = useCallback(async () => {
    const companySlug = slugify(
      userProfile?.company?.name ||
        `${userProfile?.first_name || ""}-${userProfile?.last_name || ""}`
    );
    const bookingUrl =
      profile?.scheduling_hash && companySlug
        ? `${window.location.origin}/booking/${companySlug}/${profile.scheduling_hash}`
        : "";

    if (!bookingUrl) return;

    try {
      await navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = bookingUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  }, [profile, userProfile]);

  // Edit mode handlers
  const handleEditClick = useCallback(() => {
    if (!profile) return;
    const formData = buildFormData(profile);
    setEditFormData(formData);
    originalFormDataRef.current = deepClone(formData);
    setValidationErrors({});
    setIsEditMode(true);
  }, [profile]);

  const isDirty = useCallback(() => {
    if (!editFormData || !originalFormDataRef.current) return false;
    return (
      JSON.stringify(editFormData) !==
      JSON.stringify(originalFormDataRef.current)
    );
  }, [editFormData]);

  const handleCancel = useCallback(() => {
    if (isDirty()) {
      // eslint-disable-next-line no-restricted-globals
      const confirmed = confirm(
        "You have unsaved changes. Are you sure you want to discard them?"
      );
      if (!confirmed) return;
    }
    setIsEditMode(false);
    setEditFormData(null);
    setValidationErrors({});
    originalFormDataRef.current = null;
  }, [isDirty]);

  const handleSave = useCallback(() => {
    if (!editFormData) return;

    const result = validateAll(editFormData);
    if (result.hasErrors) {
      setValidationErrors(result);
      setToast({
        show: true,
        message: "Please fix the errors before saving.",
        variant: "error",
      });
      return;
    }

    setValidationErrors({});

    // Build body with only changed fields
    const body = {};
    const orig = originalFormDataRef.current;

    if (JSON.stringify(editFormData.weeklyHours) !== JSON.stringify(orig.weeklyHours)) {
      body.weekly_hours = editFormData.weeklyHours;
    }
    if (editFormData.bufferTime !== orig.bufferTime) {
      body.min_buffer_time_minutes = Number(editFormData.bufferTime);
    }
    if (editFormData.minNotice !== orig.minNotice) {
      body.min_lead_time_minutes = Number(editFormData.minNotice);
    }
    if (editFormData.bookingWindow !== orig.bookingWindow) {
      body.max_booking_horizon_days = Number(editFormData.bookingWindow);
    }

    const durationChanged =
      JSON.stringify(editFormData.durationRules) !==
      JSON.stringify(orig.durationRules);
    if (durationChanged) {
      body.duration_rules = editFormData.durationRules;
    }

    if (Object.keys(body).length === 0) {
      // No changes
      setIsEditMode(false);
      setEditFormData(null);
      originalFormDataRef.current = null;
      return;
    }

    const requiresReEnable = isActive && durationChanged;
    dispatch(updateSchedulingProfile({ body, requiresReEnable }));
  }, [editFormData, isActive, dispatch]);

  // Form data change handlers
  const handleAvailabilityChange = useCallback((weeklyHours) => {
    setEditFormData((prev) => ({ ...prev, weeklyHours }));
  }, []);

  const handleRulesChange = useCallback(
    ({ bufferTime, minNotice, bookingWindow }) => {
      setEditFormData((prev) => ({ ...prev, bufferTime, minNotice, bookingWindow }));
    },
    []
  );

  const handleServiceConfigChange = useCallback((durationRules) => {
    setEditFormData((prev) => ({ ...prev, durationRules }));
  }, []);

  const renderHeaderActions = () => {
    if (fetchStatus !== "success") return null;
    if (isEditMode) {
      return (
        <>
          <Button
            variant="outline-secondary"
            className="cbs-header-cancel-btn"
            onClick={handleCancel}
            disabled={updateStatus === "loading"}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="cbs-header-save-btn"
            onClick={handleSave}
            disabled={updateStatus === "loading"}
          >
            {updateStatus === "loading" ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </>
      );
    }

    return (
      <>
        {!isMobile && (
          <Button
            variant="outline-secondary"
            className="cbs-header-icon-btn"
            onClick={() => setShowHowItWorks(true)}
            aria-label="How client booking works"
          >
            <Icon path={mdiHelpCircleOutline} size={0.85} />
          </Button>
        )}
        <Button
          variant="outline-secondary"
          className="cbs-header-edit-btn"
          onClick={handleEditClick}
        >
          <Icon path={mdiPencilOutline} size={0.75} className="me-1" />
          Edit
        </Button>
        {isActive ? (
          <Button
            variant="primary"
            className="cbs-header-action-btn"
            onClick={handleCopyBookingLink}
          >
            {copied ? "Copied" : "Copy Booking Link"}
          </Button>
        ) : (
          <Button
            variant="primary"
            className="cbs-header-action-btn"
            onClick={handleTurnOn}
            disabled={enableStatus === "loading"}
          >
            {enableStatus === "loading" ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Turning On...
              </>
            ) : (
              "Turn On"
            )}
          </Button>
        )}
      </>
    );
  };

  const renderTitleIcon = () => {
    if (fetchStatus !== "success") return null;
    const activeIndicator =
      !isEditMode && isActive ? (
        <span className="booking-active-indicator">
          <span className="booking-active-dot" />
          Active
        </span>
      ) : null;
    const helpIcon =
      isMobile && !isEditMode ? (
        <button
          className="cbs-how-it-works-icon"
          onClick={() => setShowHowItWorks(true)}
          aria-label="How client booking works"
        >
          <Icon path={mdiHelpCircleOutline} size={1} />
        </button>
      ) : null;
    if (!activeIndicator && !helpIcon) return null;
    return (
      <>
        {activeIndicator}
        {helpIcon}
      </>
    );
  };

  const renderBanner = () => {
    if (fetchStatus !== "success" || isEditMode || isActive) return null;
    const src = isMobile
      ? bannerImageMobile
      : isTablet
        ? bannerImageTablet
        : bannerImage;
    return (
      <div className="booking-banner">
        <img src={src} alt="Client Booking" />
      </div>
    );
  };

  const renderAvailability = () => {
    if (isEditMode && editFormData) {
      return (
        <AvailabilityEditor
          weeklyHours={editFormData.weeklyHours}
          onChange={handleAvailabilityChange}
          errors={validationErrors.availability}
        />
      );
    }

    const weeklyHours = profile?.weekly_hours || {};
    return (
      <div className="booking-settings-section">
        <h4 className="booking-settings-section-title">Online Availability</h4>
        <table className="availability-table">
          <tbody>
            {DAYS_OF_WEEK.map((day) => {
              const slots = weeklyHours[day] || [];
              return (
                <tr key={day}>
                  <td className="day-name">{day}</td>
                  <td>
                    {slots.length === 0 ? (
                      <span className="unavailable">Unavailable</span>
                    ) : (
                      slots.map((slot, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && (
                            <span className="time-slot-separator"> / </span>
                          )}
                          <span className="time-slot">
                            {formatTime(slot.start)}
                            <Icon path={mdiArrowRight} size={0.6} />
                            {formatTime(slot.end)}
                          </span>
                        </React.Fragment>
                      ))
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderBookingRules = () => {
    if (isEditMode && editFormData) {
      return (
        <BookingRulesEditor
          bufferTime={editFormData.bufferTime}
          minNotice={editFormData.minNotice}
          bookingWindow={editFormData.bookingWindow}
          onChange={handleRulesChange}
          errors={validationErrors.bookingRules}
        />
      );
    }

    const rules = [
      {
        label: "Buffer Time",
        value: formatMinutes(profile?.min_buffer_time_minutes),
        description: "Add a gap after every job",
      },
      {
        label: "Minimum Notice",
        value: formatMinutes(profile?.min_lead_time_minutes),
        description: "Limit last-minute bookings",
      },
      {
        label: "Booking Window",
        value: formatDays(profile?.max_booking_horizon_days),
        description: "How far in advance clients can book",
      },
    ];

    return (
      <div className="booking-settings-section">
        <h4 className="booking-settings-section-title">Booking Rules</h4>
        <div className="booking-rules-grid">
          {rules.map((rule, idx) => (
            <div key={idx} className="booking-rule-item">
              <div className="booking-rule-header">
                <span className="booking-rule-label">{rule.label}</span>
                <span className="booking-rule-value">{rule.value}</span>
              </div>
              <div className="booking-rule-description">
                <InfoCircle className="booking-rule-info-icon" />
                <span>{rule.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderServiceConfig = () => {
    if (isEditMode && editFormData) {
      return (
        <ServiceConfigEditor
          durationRules={editFormData.durationRules}
          onChange={handleServiceConfigChange}
          errors={validationErrors.serviceConfig}
        />
      );
    }

    const durationRules = profile?.duration_rules || [];
    return (
      <div className="booking-settings-section">
        <h4 className="booking-settings-section-title">
          Service Configuration
        </h4>
        <table className="service-config-table">
          <thead>
            <tr>
              <th>Property Size (sq ft)</th>
              <th>Base Duration (min)</th>
            </tr>
          </thead>
          <tbody>
            {durationRules.map((rule, idx) => (
              <tr key={idx}>
                <td className="service-config-size-cell">
                  {rule.max_sqft ? (
                    <>
                      <span>{rule.min_sqft.toLocaleString()}</span>
                      <Icon
                        path={mdiArrowRight}
                        size={0.55}
                        className="service-config-arrow"
                      />
                      <span>{rule.max_sqft.toLocaleString()}</span>
                    </>
                  ) : (
                    <span>{rule.min_sqft.toLocaleString()}+</span>
                  )}
                </td>
                <td>{rule.duration_minutes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderClientView = () => (
    <ClientViewPreview
      profile={profile}
      userProfile={userProfile}
      isEditMode={isEditMode}
      editFormData={editFormData}
    />
  );

  const renderSkeleton = () => (
    <div className="booking-settings-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="mb-4">
          <div
            className="skeleton-line"
            style={{ width: "40%", height: "24px" }}
          />
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "80%" }} />
          <div className="skeleton-line" style={{ width: "90%" }} />
        </div>
      ))}
    </div>
  );

  return (
    <PageLayout
      title="Client Booking"
      titleIcon={renderTitleIcon()}
      headerActions={renderHeaderActions()}
      banner={renderBanner()}
      className="cbs-page"
    >
      {fetchStatus === "loading" ? (
        renderSkeleton()
      ) : fetchStatus === "failed" ? (
        <div className="text-center text-muted mt-5">
          <p>Failed to load scheduling profile.</p>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => dispatch(fetchSchedulingProfile())}
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="booking-settings-layout">
          <div>
            {renderAvailability()}
            {renderBookingRules()}
            {renderServiceConfig()}
            {isEditMode && (
              <div className="cbs-footer-actions">
                <Button
                  variant="outline-secondary"
                  className="cbs-header-cancel-btn"
                  onClick={handleCancel}
                  disabled={updateStatus === "loading"}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="cbs-header-save-btn"
                  onClick={handleSave}
                  disabled={updateStatus === "loading"}
                >
                  {updateStatus === "loading" ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            )}
            {!isEditMode && isActive && (
              <div className="turn-off-section">
                <Button
                  variant="outline-secondary"
                  className="turn-off-btn"
                  onClick={() => setShowTurnOffModal(true)}
                >
                  Turn Off
                </Button>
              </div>
            )}
          </div>
          <div>{renderClientView()}</div>
        </div>
      )}

      <HowClientBookingWorksModal
        show={showHowItWorks}
        onHide={() => setShowHowItWorks(false)}
      />

      <TurnOffBookingModal
        show={showTurnOffModal}
        onHide={() => setShowTurnOffModal(false)}
        onConfirm={handleTurnOff}
        isLoading={disableStatus === "loading"}
      />

      <SaveSettingsModal
        show={showSaveResultModal}
        onHide={() => setShowSaveResultModal(false)}
        isActive={isActive}
        onTurnOn={handleTurnOn}
      />

      <UpgradePlanModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      <CustomToast
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        message={toast.message}
        variant={toast.variant}
        position="bottom-end"
        isMobile={isMobile}
      />
    </PageLayout>
  );
};

export default ClientBookingSettings;
